import {
  FormGroup,
  LoadingSpinner,
  Menu,
  PlainButton,
} from "@jugl-web/ui-components";
import { Button } from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import React from "react";
import { AppearanceTooltip } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/screens/WebChatBotAccountScreen/components/WebChatBotAppearanceTab/components/AppearanceTooltip";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as MoreIcon } from "./assets/more.svg";
import { ReactComponent as DisconnectIcon } from "./assets/disconnect.svg";

export type CXIntegrationTileProps = {
  title: string;
  description: string;
  icon: React.ReactNode;
  isConnected: boolean;
  needsReconnect: boolean;
  isConnecting: boolean;
  isReconnecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onReconnect: () => void;
  pendingLabel?: string;
  pendingTooltip?: string;
};

export const CXIntegrationTile: React.FC<CXIntegrationTileProps> = ({
  title,
  description,
  needsReconnect,
  isConnected,
  isConnecting,
  icon,
  onDisconnect,
  isReconnecting,
  onConnect,
  onReconnect,
  pendingLabel,
  pendingTooltip,
}) => {
  const { t } = useTranslations();

  return (
    <FormGroup
      classNames={{
        labelClassName: "text-tertiary-800",
      }}
      isInvalid={needsReconnect}
      labelPosition="border-overlap"
      label={
        isConnected && needsReconnect
          ? t({
              id: "common.disconnected",
              defaultMessage: "Disconnected",
            })
          : undefined
      }
    >
      <div
        className={cx(
          "border-grey-300 flex h-full w-full items-start gap-4 rounded-xl border border-solid p-5",
          isConnected && needsReconnect && "border-tertiary-300"
        )}
      >
        <div
          className="flex h-full w-[100px] shrink-0 items-center justify-center rounded-xl"
          style={{
            backgroundImage:
              "radial-gradient(circle, #E1E4E6 1px, #FCFCFC 1.5px)",
            backgroundSize: "8px 8px",
          }}
        >
          {icon}
        </div>
        <div className="flex flex-col overflow-hidden">
          <div className="flex items-center justify-between gap-2.5">
            <span className="font-secondary truncate text-xl font-medium leading-[30px] text-black">
              {title}
            </span>
            <div className="flex items-center gap-2.5">
              {isConnected && (
                <Menu
                  autoClose
                  placement="bottom"
                  renderTrigger={({ Trigger, triggerRef, isOpen }) => (
                    <Trigger
                      ref={triggerRef}
                      as={PlainButton}
                      className={cx(
                        "flex h-8 w-8 items-center justify-center rounded-[10px] border border-solid border-[#F2F2F2] ring-inset ring-[#F2F2F2] transition hover:ring-1",
                        isOpen && "ring-1"
                      )}
                    >
                      <MoreIcon />
                    </Trigger>
                  )}
                  sections={[
                    [
                      {
                        id: "disconnect",
                        label: t({
                          id: "common.disconnect",
                          defaultMessage: "Disconnect",
                        }),
                        icon: <DisconnectIcon />,
                        onSelect: onDisconnect,
                      },
                    ],
                  ]}
                />
              )}
            </div>
          </div>
          <span className="font-secondary mt-2 text-sm leading-[21px] text-[#737B7E]">
            {description}
          </span>
          <div className="mt-5">
            {!isConnected ? (
              <Button
                variant="secondary"
                className="h-8"
                onClick={onConnect}
                isDisabled={isConnecting}
                iconEnd={
                  isConnecting ? (
                    <LoadingSpinner size="xs" color="grey" />
                  ) : (
                    <ArrowIcon />
                  )
                }
              >
                {isConnecting
                  ? t({
                      id: "common.connecting",
                      defaultMessage: "Connecting...",
                    })
                  : t({
                      id: "common.connect",
                      defaultMessage: "Connect",
                    })}
              </Button>
            ) : needsReconnect ? (
              <div className="flex items-center gap-4">
                <Button
                  variant="secondary"
                  className="h-8"
                  iconEnd={
                    isReconnecting ? (
                      <LoadingSpinner size="xs" color="grey" />
                    ) : (
                      <ArrowIcon />
                    )
                  }
                  isDisabled={isReconnecting}
                  onClick={onReconnect}
                >
                  {isReconnecting
                    ? t({
                        id: "cx-actions-page.reconnecting",
                        defaultMessage: "Reconnecting...",
                      })
                    : pendingLabel ??
                      t({
                        id: "cx-actions-page.reconnect",
                        defaultMessage: "Reconnect",
                      })}
                </Button>
                <AppearanceTooltip>
                  {pendingTooltip ??
                    t(
                      {
                        id: "cx-actions-page.account-disconnected",
                        defaultMessage:
                          "Account was disconnected. All related actions are disabled. Reconnect to your <underlineText>{integration} account</underlineText> to enable the Actions",
                      },
                      {
                        integration: title,
                        underlineText: (text: (string | JSX.Element)[]) => (
                          <span className="underline">{text}</span>
                        ),
                      }
                    )}
                </AppearanceTooltip>
              </div>
            ) : (
              <div className="flex h-8 w-max items-center gap-2.5 rounded-[10px] border border-solid border-[#F2F2F2] px-4">
                <div className="h-2.5 w-2.5 rounded-full bg-[#22C59E]" />
                <span className="text-dark-800 font-secondary text-sm font-medium">
                  {t({
                    id: "common.connected",
                    defaultMessage: "Connected",
                  })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </FormGroup>
  );
};
